import { useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  CheckCircle2,
  Clock,
  XCircle,
  Upload,
  ExternalLink,
  Sparkles,
  Star,
} from 'lucide-react';

import { useUserStore } from '@/store/userStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import {
  canSeeRewards,
  getRewardTaskOrder,
  getTaskCtaUrl,
  taskRequiresProofUrl,
  validateProofUrl,
} from '@/utils/rewardsHelpers';
import { RewardTaskType, ReviewSubmission } from '@/types';
import { apiService } from '@/api';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui';
import { useReviewSubmissions } from './useReviewSubmissions';
import {
  AppStoreLogo,
  GooglePlayLogo,
  GoogleLogo,
  XLogo,
  RedditLogo,
  LinkedInLogo,
  FacebookLogo,
  PinterestLogo,
  ChromeLogo,
  OpenAILogo,
  TrustpilotLogo,
} from '@/components/brands/BrandLogos';

interface TaskDefinition {
  type: RewardTaskType;
  titleKey: string;
  /** Official platform mark (simple-icons / store logos), tinted via brandColor. */
  Logo: React.ComponentType<{ className?: string }>;
  brandColor: string;
  stepCount: number;
  family: 'serp' | 'social' | 'review';
}

const TASK_DEFINITIONS: Record<RewardTaskType, TaskDefinition> = {
  google_preferred_source: {
    type: 'google_preferred_source',
    titleKey: 'rewards.tasks.google_preferred_source.title',
    Logo: GoogleLogo,
    brandColor: '#4285F4',
    stepCount: 3,
    family: 'serp',
  },
  share_x: {
    type: 'share_x',
    titleKey: 'rewards.tasks.share_x.title',
    Logo: XLogo,
    brandColor: '#E7E9EA',
    stepCount: 3,
    family: 'social',
  },
  share_reddit: {
    type: 'share_reddit',
    titleKey: 'rewards.tasks.share_reddit.title',
    Logo: RedditLogo,
    brandColor: '#FF4500',
    stepCount: 3,
    family: 'social',
  },
  share_linkedin: {
    type: 'share_linkedin',
    titleKey: 'rewards.tasks.share_linkedin.title',
    Logo: LinkedInLogo,
    brandColor: '#0A66C2',
    stepCount: 3,
    family: 'social',
  },
  share_facebook: {
    type: 'share_facebook',
    titleKey: 'rewards.tasks.share_facebook.title',
    Logo: FacebookLogo,
    brandColor: '#1877F2',
    stepCount: 3,
    family: 'social',
  },
  share_pinterest: {
    type: 'share_pinterest',
    titleKey: 'rewards.tasks.share_pinterest.title',
    Logo: PinterestLogo,
    brandColor: '#E60023',
    stepCount: 3,
    family: 'social',
  },
  review_ios: {
    type: 'review_ios',
    titleKey: 'rewards.tasks.review_ios.title',
    Logo: AppStoreLogo,
    brandColor: '#0A84FF',
    stepCount: 4,
    family: 'review',
  },
  review_android: {
    type: 'review_android',
    titleKey: 'rewards.tasks.review_android.title',
    Logo: GooglePlayLogo,
    brandColor: '#34A853',
    stepCount: 4,
    family: 'review',
  },
  review_chrome: {
    type: 'review_chrome',
    titleKey: 'rewards.tasks.review_chrome.title',
    Logo: ChromeLogo,
    brandColor: '#4285F4',
    stepCount: 4,
    family: 'review',
  },
  review_chatgpt: {
    type: 'review_chatgpt',
    titleKey: 'rewards.tasks.review_chatgpt.title',
    Logo: OpenAILogo,
    brandColor: '#10A37F',
    stepCount: 4,
    family: 'review',
  },
  review_trustpilot: {
    type: 'review_trustpilot',
    titleKey: 'rewards.tasks.review_trustpilot.title',
    Logo: TrustpilotLogo,
    brandColor: '#00B67A',
    stepCount: 4,
    family: 'review',
  },
};

function CreditsBadge({
  credits,
  size = 'md',
}: {
  credits: number;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-bold text-white whitespace-nowrap',
        'bg-gradient-to-r from-primary to-accent shadow-[0_0_12px_rgba(139,92,246,0.4)]',
        size === 'sm' && 'px-2.5 py-1 text-xs',
        size === 'md' && 'px-3 py-1.5 text-sm',
        size === 'lg' &&
          'px-4 py-2 text-base md:text-lg gap-1.5 shadow-[0_0_20px_rgba(139,92,246,0.6)]',
      )}
    >
      <Sparkles
        className={cn(
          size === 'sm' && 'w-3 h-3',
          size === 'md' && 'w-3.5 h-3.5',
          size === 'lg' && 'w-5 h-5',
        )}
      />
      +{credits} {t('rewards.creditsShort')}
    </span>
  );
}

function ProBadge({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-bold tracking-wide whitespace-nowrap',
        'bg-gradient-to-r from-primary to-accent text-white shadow-[0_0_12px_rgba(139,92,246,0.35)]',
        size === 'sm' ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1 text-xs',
      )}
    >
      PRO
    </span>
  );
}

export function RewardsPage() {
  const { user, refreshUser } = useUserStore();
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);

  if (!canSeeRewards(user, activeOrgId)) {
    return <Navigate to="/" replace />;
  }

  return (
    <RewardsPageInner
      userId={user!.id}
      abGroup={user!.abGroup}
      refreshUser={refreshUser}
    />
  );
}

function RewardsPageInner({
  userId,
  abGroup,
  refreshUser,
}: {
  userId: string;
  abGroup?: number;
  refreshUser: () => Promise<void>;
}) {
  const language = useSettingsStore((s) => s.language);
  const { latestByTask, totalApprovedCredits, refresh, isLoading } =
    useReviewSubmissions(userId);

  const orderedTasks = useMemo(
    () => getRewardTaskOrder(userId, abGroup),
    [userId, abGroup],
  );
  const totalCredits = useMemo(
    () => orderedTasks.reduce((sum, t) => sum + t.rewardCredits, 0),
    [orderedTasks],
  );

  // Progress counts credits of submitted tasks (pending + approved). Rejected
  // don't count until the user resubmits. Use latest-per-task so a resubmit
  // replaces reject.
  const progressCredits = useMemo(() => {
    return orderedTasks.reduce((sum, { type, rewardCredits }) => {
      const s = latestByTask[type];
      return s && (s.status === 'pending' || s.status === 'approved')
        ? sum + rewardCredits
        : sum;
    }, 0);
  }, [orderedTasks, latestByTask]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <RewardsHero
        earnedCredits={totalApprovedCredits}
        progressCredits={progressCredits}
        totalCredits={totalCredits}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {orderedTasks.map(({ type, rewardCredits }) => (
          <TaskCard
            key={type}
            task={TASK_DEFINITIONS[type]}
            rewardCredits={rewardCredits}
            featured={type === 'review_trustpilot'}
            submission={latestByTask[type]}
            userId={userId}
            language={language}
            abGroup={abGroup}
            onSubmitted={async () => {
              await refresh();
              await refreshUser();
            }}
            isInitialLoading={isLoading && !latestByTask[type]}
          />
        ))}
      </div>
    </div>
  );
}

function RewardsHero({
  earnedCredits,
  progressCredits,
  totalCredits,
}: {
  earnedCredits: number;
  progressCredits: number;
  totalCredits: number;
}) {
  const { t } = useTranslation();
  const pct = Math.min(
    100,
    Math.round((progressCredits / Math.max(totalCredits, 1)) * 100),
  );

  return (
    <div className="mb-6 md:mb-8 p-5 md:p-6 rounded-2xl bg-surface border border-glass-border">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-xl md:text-2xl font-bold text-text-primary">
              {t('rewards.title')}
            </h1>
            <ProBadge />
          </div>
          <p className="text-sm md:text-base text-text-secondary leading-relaxed">
            {t('rewards.subtitle', { total: totalCredits })}
          </p>
        </div>
        {earnedCredits > 0 && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs uppercase tracking-wider text-text-tertiary font-semibold">
              {t('rewards.earnedLabel')}
            </span>
            <CreditsBadge credits={earnedCredits} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-semibold text-text-primary">
            {t('rewards.progressLabel', {
              approved: progressCredits,
              total: totalCredits,
            })}
          </span>
          <span className="text-text-tertiary">
            {t('rewards.progressHint', { total: totalCredits })}
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-surface-light overflow-hidden border border-glass-border">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function TaskCard({
  task,
  rewardCredits,
  featured = false,
  submission,
  userId,
  language,
  abGroup,
  onSubmitted,
  isInitialLoading,
}: {
  task: TaskDefinition;
  rewardCredits: number;
  featured?: boolean;
  submission?: ReviewSubmission;
  userId: string;
  language: string;
  abGroup?: number;
  onSubmitted: () => Promise<void>;
  isInitialLoading: boolean;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState(submission?.proof_url || '');

  const status = submission?.status ?? 'none';
  const canUpload = status !== 'pending' && status !== 'approved';
  const requiresProofUrl = taskRequiresProofUrl(task.type);
  const ctaUrl = useMemo(
    () => getTaskCtaUrl(task.type, { language, abGroup }),
    [task.type, language, abGroup],
  );
  const Logo = task.Logo;

  const proofErrorKey = (code: string | null): string | null => {
    if (!code) return null;
    const key = `rewards.errors.${code}`;
    const translated = t(key);
    return translated === key ? t('rewards.errors.proofUrlInvalid') : translated;
  };

  const handleFilePicked = async (file: File) => {
    setError(null);
    if (requiresProofUrl) {
      const validation = validateProofUrl(task.type, proofUrl);
      if (validation) {
        setError(proofErrorKey(validation));
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const fileType = file.type || 'image/png';
      const { submissionId, uploadUrl, s3Key } = await apiService.getReviewUploadUrl(
        userId,
        task.type,
        file.name,
        fileType,
      );

      const arrayBuffer = await file.arrayBuffer();
      await axios.put(uploadUrl, arrayBuffer, {
        headers: { 'Content-Type': fileType },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });

      await apiService.submitReview(
        userId,
        submissionId,
        task.type,
        s3Key,
        requiresProofUrl ? proofUrl.trim() : '',
      );

      await onSubmitted();
    } catch (e) {
      console.error('upload failed', e);
      const axErr = e as { response?: { data?: { error?: string } }; message?: string };
      setError(axErr.response?.data?.error || axErr.message || t('rewards.errors.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const stepKeys = Array.from({ length: task.stepCount }, (_, i) => i + 1);

  return (
    <div
      className={cn(
        'rounded-2xl bg-surface border flex flex-col gap-4 transition-colors relative overflow-hidden',
        featured ? 'p-5 md:p-6 border-2' : 'p-5 md:p-6',
      )}
      style={
        featured
          ? {
              borderColor: `${task.brandColor}80`,
              background: `linear-gradient(135deg, ${task.brandColor}14 0%, rgba(31,31,46,1) 55%)`,
              boxShadow: `0 0 18px ${task.brandColor}26`,
            }
          : { borderColor: `${task.brandColor}33` }
      }
    >
      {featured && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 animate-[shimmer_9s_linear_infinite] bg-[length:220%_100%] bg-[linear-gradient(110deg,transparent_42%,rgba(255,255,255,0.05)_50%,transparent_58%)]"
        />
      )}

      {featured && (
        <div className="relative z-10 flex items-center justify-between gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
            style={{
              background: `linear-gradient(90deg, ${task.brandColor}, #F59E0B)`,
            }}
          >
            <Sparkles className="w-3 h-3" />
            {t('rewards.tasks.review_trustpilot.badge')}
          </span>
          <span className="inline-flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className="w-4 h-4 fill-[#FFC107] text-[#FFC107]"
              />
            ))}
          </span>
        </div>
      )}

      <div className="relative z-10 flex items-start gap-3">
        <div
          className={cn(
            'rounded-xl border flex items-center justify-center flex-shrink-0',
            featured ? 'w-12 h-12 md:w-14 md:h-14' : 'w-12 h-12',
          )}
          style={{
            backgroundColor: `${task.brandColor}1F`,
            borderColor: `${task.brandColor}55`,
            color: task.brandColor,
          }}
        >
          <Logo className={cn(featured ? 'w-6 h-6 md:w-7 md:h-7' : 'w-6 h-6')} />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              'font-semibold text-text-primary leading-snug',
              featured ? 'text-base md:text-lg font-bold' : 'text-base md:text-lg',
            )}
          >
            {t(task.titleKey)}
          </h3>
          {featured && (
            <p className="mt-1 text-xs md:text-sm text-text-secondary leading-relaxed">
              {t('rewards.tasks.review_trustpilot.tagline')}
            </p>
          )}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <CreditsBadge
              credits={rewardCredits}
              size={featured ? 'md' : 'sm'}
            />
            <ProBadge size="sm" />
            <StatusPill status={status} />
          </div>
        </div>
      </div>

      {/* Steps */}
      <ol className="relative z-10 space-y-2">
        {stepKeys.map((n) => (
          <Step
            key={n}
            number={n}
            text={t(`rewards.tasks.${task.type}.step${n}`)}
          />
        ))}
      </ol>

      {/* Proof hint */}
      <p className="relative z-10 text-xs text-text-tertiary leading-relaxed">
        {t(`rewards.tasks.${task.type}.proofHint`)}
      </p>

      {/* Proof URL */}
      {canUpload && requiresProofUrl && (
        <div className="relative z-10 space-y-1.5">
          <label className="text-xs font-semibold text-text-secondary">
            {t('rewards.proofUrlLabel')}
          </label>
          <input
            type="url"
            value={proofUrl}
            onChange={(e) => setProofUrl(e.target.value)}
            placeholder={t(`rewards.tasks.${task.type}.proofPlaceholder`)}
            disabled={uploading}
            className="w-full px-3 py-2.5 rounded-xl bg-surface-light border border-glass-border text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary/50"
          />
        </div>
      )}

      {status === 'rejected' && submission?.review_notes && (
        <div className="relative z-10 px-3 py-2 rounded-lg bg-surface-light border border-glass-border text-sm text-text-secondary">
          <span className="text-text-tertiary text-xs uppercase tracking-wider mr-2">
            {t('rewards.reviewerNote')}
          </span>
          {submission.review_notes}
        </div>
      )}

      {error && (
        <div className="relative z-10 px-3 py-2 rounded-lg bg-status-error/10 border border-status-error/30 text-sm text-status-error">
          {error}
        </div>
      )}

      <div className="relative z-10 flex flex-col sm:flex-row gap-2 mt-auto">
        <a
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-surface-light hover:bg-surface text-text-primary border border-glass-border transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          {t(`rewards.tasks.${task.type}.cta`)}
        </a>

        {canUpload ? (
          <Button
            variant="primary"
            size="md"
            isLoading={uploading}
            disabled={isInitialLoading || (requiresProofUrl && !proofUrl.trim())}
            leftIcon={!uploading ? <Upload className="w-4 h-4" /> : undefined}
            onClick={() => fileInputRef.current?.click()}
            className="flex-1"
          >
            {uploading
              ? t('rewards.uploading', { progress: uploadProgress })
              : status === 'rejected'
                ? t('rewards.resubmit')
                : t('rewards.uploadScreenshot')}
          </Button>
        ) : (
          <div className="flex-1 inline-flex flex-col items-center justify-center gap-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-surface-light text-text-secondary border border-glass-border text-center">
            {status === 'approved' ? (
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-text-primary" />
                {t('rewards.alreadyApproved')}
              </span>
            ) : (
              <>
                <span className="inline-flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t('rewards.status.pending')}
                </span>
                <span className="text-xs text-text-tertiary font-normal leading-snug">
                  {t('rewards.awaitingReview')}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFilePicked(file);
        }}
      />
    </div>
  );
}

function Step({ number, text }: { number: number; text: string }) {
  return (
    <li className="flex items-start gap-3 text-sm text-text-secondary">
      <span className="w-5 h-5 rounded-full border border-glass-border bg-surface-light text-text-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
        {number}
      </span>
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}

function StatusPill({
  status,
}: {
  status: 'none' | 'pending' | 'approved' | 'rejected';
}) {
  const { t } = useTranslation();
  if (status === 'none') return null;

  const config = {
    pending: { Icon: Clock, label: t('rewards.status.pending') },
    approved: { Icon: CheckCircle2, label: t('rewards.status.approved') },
    rejected: { Icon: XCircle, label: t('rewards.status.rejected') },
  } as const;

  const c = config[status];

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-surface-light border-glass-border text-text-secondary">
      <c.Icon className="w-3.5 h-3.5" />
      {c.label}
    </span>
  );
}
